import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type { Analytics, Envelope, InventoryRow, Manager, Owner, Period, Site, Subscription } from './types';

// TanStack Query is here for a reason stated in PRODUCT.md, not as a nicety: the
// audience is on a bad Indian mobile link, and Render sleeps on the free tier.
// Retries and stale-while-revalidate are what stop a dashboard of six tiles all
// failing at once on a cold start.

export const keys = {
  analytics: (days: Period, site: string | null) => ['analytics', days, site] as const,
  subscription: ['subscription'] as const,
  owners: ['owners'] as const,
  managers: (scope: 'default' | 'all') => ['managers', scope] as const,
  sites: ['sites'] as const,
};

export function useAnalytics(days: Period, site: string | null) {
  return useQuery({
    queryKey: keys.analytics(days, site),
    queryFn: () => api<Envelope<Analytics>>(
      `/api/reports/analytics?days=${days}${site ? `&site=${encodeURIComponent(site)}` : ''}`
    ).then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: keys.subscription,
    // Mounted OUTSIDE the subscription gate on the server, so this still answers
    // when everything else is returning 402 — which is exactly when it matters.
    queryFn: () => api<Envelope<Subscription>>('/api/org/subscription').then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

export function useOwners() {
  return useQuery({
    queryKey: keys.owners,
    queryFn: () => api<Envelope<Owner[]>>('/api/users/owners').then((r) => r.data),
  });
}

export function useManagers(scope: 'default' | 'all' = 'all') {
  return useQuery({
    queryKey: keys.managers(scope),
    queryFn: () => api<Envelope<Manager[]>>(
      `/api/users/managers${scope === 'all' ? '?scope=all' : ''}`
    ).then((r) => r.data),
  });
}

export function useSites() {
  return useQuery({
    queryKey: keys.sites,
    queryFn: () => api<Envelope<Site[]>>('/api/sites').then((r) => r.data),
  });
}

/** Super admin only. The server refuses anyone else, and refuses a manager target. */
export function useSetWebAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      api<{ success: boolean; message: string }>(`/api/users/${userId}/web-access`, {
        method: 'PUT', body: { enabled },
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.owners }); },
  });
}

export type TempPassword = { name: string; phone: string; tempPassword: string };

/**
 * Owner or Super Admin sets a new password for one of their managers.
 *
 * A manager has no self-service route: `forgot-password` matches only owners and
 * super admins, and a manager signs in by phone, so an emailed code could not
 * reach them anyway. Before this, a manager who forgot their password was locked
 * out permanently and nobody could help.
 *
 * The password comes back exactly once and is never stored anywhere readable, so
 * the caller must show it immediately — there is no second chance to fetch it.
 */
export function useResetManagerPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<Envelope<TempPassword>>(`/api/users/managers/${userId}/reset-password`, { method: 'POST' })
        .then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.managers('all') }); },
  });
}

export type PendingTransfer = {
  to_user_id: string; to_name: string; expires_at: string; attempts_left: number;
} | null;

export function useTransfer() {
  return useQuery({
    queryKey: ['transfer'],
    queryFn: () => api<{ success: boolean; data: PendingTransfer }>('/api/org/super-admin/transfer').then((r) => r.data),
    // Super admin only; anyone else gets a 403 that must not be retried.
    retry: false,
  });
}

export function useTransferActions() {
  const qc = useQueryClient();
  const done = () => { qc.invalidateQueries({ queryKey: ['transfer'] }); };
  return {
    request: useMutation({
      mutationFn: (toUserId: string) =>
        api<{ message: string; data: { to_name: string; sent_to: string } }>(
          '/api/org/super-admin/transfer/request', { method: 'POST', body: { toUserId } }),
      onSuccess: done,
    }),
    confirm: useMutation({
      mutationFn: (code: string) =>
        api<{ message: string }>('/api/org/super-admin/transfer/confirm', { method: 'POST', body: { code } }),
      onSuccess: done,
    }),
    cancel: useMutation({
      mutationFn: () => api<{ message: string }>('/api/org/super-admin/transfer/cancel', { method: 'POST' }),
      onSuccess: done,
    }),
  };
}

export function useInventory(siteName: string | null) {
  return useQuery({
    queryKey: ['inventory', siteName],
    queryFn: () => api<Envelope<InventoryRow[]>>(`/api/inventory/${encodeURIComponent(siteName!)}`).then((r) => r.data),
    enabled: Boolean(siteName),
  });
}

/** Retry everything except the errors where retrying cannot help. */
export function shouldRetry(failureCount: number, error: unknown) {
  if (error instanceof ApiError) {
    if (error.isUnauthenticated || error.isWebAccessDenied || error.isSubscriptionInactive) return false;
    if (error.status >= 400 && error.status < 500) return false;
  }
  return failureCount < 3;
}
